import MiniSearch from 'minisearch'
import type { SearchResult } from './types'

let searchIndex: MiniSearch<SearchDocument> | null = null
let allDocuments: SearchDocument[] = []
let shuffledIndices: number[] = [] // Session-sticky random order
let isLoading = false
let loadPromise: Promise<void> | null = null

interface SearchDocument {
  id: string
  type: string
  title: string
  authors: string
  abstract: string
}

/**
 * Fisher-Yates shuffle algorithm for creating random order
 */
function createShuffledIndices(length: number): number[] {
  const indices = Array.from({ length }, (_, i) => i)
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }
  return indices
}

export interface SearchOptions {
  limit?: number
  fuzzy?: number
  prefix?: boolean
}

/**
 * Initialize the search index
 */
export async function initSearch(): Promise<void> {
  if (searchIndex) return
  if (loadPromise) return loadPromise
  
  isLoading = true
  loadPromise = (async () => {
    try {
      const startTime = performance.now()
      
      const response = await fetch('/data/search_index.json')
      if (!response.ok) {
        throw new Error(`Failed to load search index: ${response.status}`)
      }
      
      const data = await response.json()
      const fetchTime = performance.now() - startTime
      
      // Build index from documents
      const buildStart = performance.now()
      
      searchIndex = new MiniSearch<SearchDocument>({
        fields: ['title', 'authors', 'abstract'],
        storeFields: ['id', 'type', 'title', 'authors', 'abstract'],
        searchOptions: {
          boost: { title: 10, authors: 5, abstract: 1 },
          fuzzy: 0.2,
          prefix: true,
        }
      })
      
      allDocuments = data.documents
      searchIndex.addAll(allDocuments)
      
      // Create session-sticky shuffled order for random display
      shuffledIndices = createShuffledIndices(allDocuments.length)
      
      const buildTime = performance.now() - buildStart
      
      console.log(`✓ Search index loaded (fetch: ${fetchTime.toFixed(0)}ms, build: ${buildTime.toFixed(0)}ms, ${allDocuments.length} docs)`)
    } catch (e) {
      console.error('Failed to initialize search:', e)
      throw e
    } finally {
      isLoading = false
    }
  })()
  
  return loadPromise
}

/**
 * Search for papers
 */
export function search(query: string, options: SearchOptions = {}): SearchResult[] {
  if (!searchIndex) {
    console.warn('Search index not loaded')
    return []
  }
  
  const { limit = 20, fuzzy = 0.2, prefix = true } = options
  
  if (!query.trim()) {
    // Return ALL documents in session-sticky random order
    return shuffledIndices.map(idx => {
      const doc = allDocuments[idx]
      return {
        id: doc.id,
        type: doc.type,
        title: doc.title,
        authors: doc.authors,
        abstract: doc.abstract,
        score: 1,
      }
    })
  }
  
  // Search with AND by default (all terms must match)
  let results = searchIndex.search(query, {
    boost: { title: 10, authors: 5, abstract: 1 },
    fuzzy,
    prefix,
    combineWith: 'AND',
  })
  
  // If no results with AND, try OR
  if (results.length === 0) {
    results = searchIndex.search(query, {
      boost: { title: 10, authors: 5, abstract: 1 },
      fuzzy,
      prefix,
      combineWith: 'OR',
    })
  }
  
  return results.slice(0, limit).map(result => ({
    id: result.id as string,
    type: result.type as string,
    title: result.title as string,
    authors: result.authors as string,
    abstract: result.abstract as string,
    score: result.score,
  }))
}

/**
 * Get autocomplete suggestions
 */
export function suggest(query: string, limit = 5): SearchResult[] {
  if (!searchIndex || !query.trim()) return []
  
  const results = searchIndex.autoSuggest(query, {
    boost: { title: 10, authors: 5 },
    fuzzy: 0.1,
  })
  
  // autoSuggest returns {suggestion, score, terms}[], not document results
  // So we search for each suggestion
  const seen = new Set<string>()
  const suggestions: SearchResult[] = []
  
  for (const result of results.slice(0, limit)) {
    const docs = searchIndex.search(result.suggestion, { 
      prefix: true,
      boost: { title: 10 },
    }).slice(0, 1)
    
    for (const doc of docs) {
      if (!seen.has(doc.id as string)) {
        seen.add(doc.id as string)
        suggestions.push({
          id: doc.id as string,
          type: doc.type as string,
          title: doc.title as string,
          authors: doc.authors as string,
          abstract: doc.abstract as string,
          score: doc.score,
        })
      }
    }
  }
  
  return suggestions
}

/**
 * Check if search is ready
 */
export function isSearchReady(): boolean {
  return searchIndex !== null
}

/**
 * Check if search is loading
 */
export function isSearchLoading(): boolean {
  return isLoading
}

/**
 * Get total document count
 */
export function getDocumentCount(): number {
  return allDocuments.length
}

/**
 * Reshuffle the random order for empty query results
 */
export function reshuffleDocuments(): void {
  if (allDocuments.length > 0) {
    shuffledIndices = createShuffledIndices(allDocuments.length)
  }
}
